import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Loader2,
  Calendar,
  AlertCircle,
  ExternalLink,
  ChevronDown,
  ChevronRight,
  CircleDollarSign,
  CreditCard,
  Building2,
  Banknote,
  FileCheck,
  Clock,
  User,
  Users,
  UserCheck,
  HelpCircle,
  Gift,
} from 'lucide-react';
import { Badge } from '../../../components/ui/badge';
import { ProviderBadge, deriveProvider } from '../../../components/financials/ProviderBadge';
import { MarkEntryPaidButton } from '../../../components/admin/MarkEntryPaidButton';
import { EntryAdminActions } from '../../../components/admin/EntryAdminActions';
import {
  useAdminUserEntries,
  type AdminEntryRow,
  type LedgerCreatedByRole,
  type LedgerPaymentMethod,
  type PaymentStatus,
} from '../../../api/extensions/useAdminEntries';
import { type AdminUser } from '../../../api/extensions/useAdminOps';

function formatCurrency(amount: number, currency = 'USD'): string {
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount);
  } catch {
    return `$${amount.toFixed(2)}`;
  }
}

function formatDateRange(start?: string | null, end?: string | null): string {
  if (!start && !end) return '—';
  const fmt = (d: string) => new Date(d).toLocaleDateString();
  if (start && end) {
    if (start === end) return fmt(start);
    return `${fmt(start)} – ${fmt(end)}`;
  }
  return fmt((start || end) as string);
}

const STATUS_META: Record<PaymentStatus, { label: string; cls: string }> = {
  paid: {
    label: 'Paid',
    cls: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30',
  },
  unpaid: {
    label: 'Unpaid',
    cls: 'bg-rose-500/15 text-rose-700 dark:text-rose-300 border-rose-500/30',
  },
  partial: {
    label: 'Partial',
    cls: 'bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30',
  },
  // In-flight ACH/bank debit — submitted to Stripe, awaiting settlement.
  processing: {
    label: 'Processing',
    cls: 'bg-sky-500/15 text-sky-700 dark:text-sky-300 border-sky-500/30',
  },
  // ACH micro-deposit awaiting the contestant's bank verification.
  verifying: {
    label: 'Verifying bank',
    cls: 'bg-indigo-500/15 text-indigo-700 dark:text-indigo-300 border-indigo-500/30',
  },
  free: {
    label: 'Free',
    cls: 'bg-slate-500/15 text-slate-700 dark:text-slate-300 border-slate-500/30',
  },
};

interface EntriesTabProps {
  user: AdminUser;
}

export default function EntriesTab({ user }: EntriesTabProps) {
  const { data, isLoading, error } = useAdminUserEntries(user.id);

  // Row-expansion state. Keyed by registration_id so it survives re-fetches.
  // Multiple rows can be open at once so admins can compare entries
  // side-by-side (e.g. checking whether two team-roping legs were both paid
  // by the same partner).
  const [expandedRows, setExpandedRows] = useState<Set<string>>(
    () => new Set(),
  );
  const toggleRow = useCallback((id: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const grouped = useMemo(() => {
    if (!data) return null;
    return {
      current: groupByOrgRodeo(data.current),
      past: groupByOrgRodeo(data.past),
    };
  }, [data]);

  if (isLoading) {
    return (
      <div className="rounded-lg border bg-card p-12 text-center">
        <Loader2 className="h-6 w-6 animate-spin mx-auto text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-destructive/30 bg-destructive/10 p-4 flex items-start gap-3">
        <AlertCircle className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
        <div>
          <h3 className="font-medium text-destructive">Couldn't load entries</h3>
          <p className="text-sm text-destructive/80 mt-1">
            {(error as Error).message || 'Unknown error'}
          </p>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const hasCurrent = data.current.length > 0;
  const hasPast = data.past.length > 0;
  // `?? 0` on the newer totals so a stale backend renders $0 instead of NaN
  // until the server catches up.
  const refunded = data.totals.totalRefunded ?? 0;
  const outstanding = data.totals.totalOutstanding ?? 0;
  const retainedOnCancelled = data.totals.totalRetainedOnCancelled ?? 0;
  // Lifetime gross = net + refunded by construction, so no extra API field is
  // needed. Shown in the reconciliation line under the cards: the headline
  // stays net (that's what makes paid + outstanding ≈ due reconcile) while
  // the gross matches what the Financials tab shows as "Paid", instead of the
  // two tabs silently disagreeing by the refunds.
  const paidLifetime = data.totals.totalPaid + refunded;

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          <SummaryCard label="Current entries" value={String(data.totals.currentCount)} />
          <SummaryCard label="Past entries" value={String(data.totals.pastCount)} />
          <SummaryCard
            label="Total fees due"
            value={formatCurrency(data.totals.totalDue)}
            accent="text-amber-700 dark:text-amber-300"
            hint="active entries"
          />
          {/* Refunds get their own card rather than being folded into Total
              fees paid — otherwise refunded money reads as money the
              contestant still handed over, and "fees paid" can exceed "fees
              due" with nothing on the tab explaining the gap. */}
          <SummaryCard
            label="Total fees paid"
            value={formatCurrency(data.totals.totalPaid)}
            accent="text-emerald-700 dark:text-emerald-300"
            hint="net of refunds"
          />
          <SummaryCard
            label="Refunded"
            value={formatCurrency(refunded)}
            accent={
              refunded > 0.01 ? 'text-rose-700 dark:text-rose-300' : undefined
            }
          />
          <SummaryCard
            label="Outstanding"
            value={formatCurrency(outstanding)}
            accent={
              outstanding > 0.01
                ? 'text-rose-700 dark:text-rose-300'
                : undefined
            }
            hint="still owed"
          />
        </div>
        {/* Bridge to the Financials tab, which reports payments GROSS. Without
            this line the two tabs quietly disagree by exactly the refunded
            amount, and on an account where the refunds happen to equal the
            surviving entries every card reads the same number for three
            different reasons. */}
        {refunded > 0.01 && (
          <p className="text-xs text-muted-foreground">
            {formatCurrency(paidLifetime)} paid lifetime (the Paid figure on
            the Financials tab) − {formatCurrency(refunded)} refunded ={' '}
            <span className="font-medium text-foreground">
              {formatCurrency(data.totals.totalPaid)}
            </span>{' '}
            net.
          </p>
        )}
        {/* The one gap the cards can't explain on their own: money kept on an
            entry the contestant backed out of sits inside Total fees paid but
            has no line in Total fees due (active entries only), so paid can
            legitimately run ahead of due. Only rendered when there's actually
            a residue to account for. */}
        {retainedOnCancelled > 0.01 && (
          <p className="text-xs text-muted-foreground">
            {formatCurrency(retainedOnCancelled)} of the paid total was kept on
            cancelled entries, which aren't counted in fees due.
          </p>
        )}
      </div>

      <Section title="Current entries" empty={!hasCurrent} emptyMessage="Not entered in any upcoming events.">
        {grouped &&
          grouped.current.map((group) => (
            <RodeoGroup
              key={`current-${group.key}`}
              group={group}
              expandedRows={expandedRows}
              onToggleRow={toggleRow}
            />
          ))}
      </Section>

      <Section title="Past entries" empty={!hasPast} emptyMessage="No previous entries on file.">
        {grouped &&
          grouped.past.map((group) => (
            <RodeoGroup
              key={`past-${group.key}`}
              group={group}
              expandedRows={expandedRows}
              onToggleRow={toggleRow}
            />
          ))}
      </Section>
    </div>
  );
}

function SummaryCard({
  label,
  value,
  accent,
  hint,
}: {
  label: string;
  value: string;
  accent?: string;
  hint?: string;
}) {
  return (
    <div className="rounded-lg border bg-card text-card-foreground shadow-sm p-4 flex flex-col">
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="flex-1 flex flex-col items-center justify-center text-center mt-2">
        <div className={`text-2xl font-bold tabular-nums ${accent ?? ''}`}>{value}</div>
        {hint && (
          <div className="text-[11px] text-muted-foreground mt-1">{hint}</div>
        )}
      </div>
    </div>
  );
}

function Section({
  title,
  empty,
  emptyMessage,
  children,
}: {
  title: string;
  empty: boolean;
  emptyMessage: string;
  children: React.ReactNode;
}) {
  return (
    <section className="space-y-3">
      <h2 className="font-semibold text-sm uppercase tracking-wide text-muted-foreground">
        {title}
      </h2>
      {empty ? (
        <div className="rounded-lg border bg-card p-8 text-center text-sm text-muted-foreground">
          {emptyMessage}
        </div>
      ) : (
        <div className="space-y-4">{children}</div>
      )}
    </section>
  );
}

interface RodeoGroupItem {
  key: string;
  organizationName: string | null;
  rodeoId: string | null;
  rodeoName: string | null;
  rodeoStartDate: string | null;
  rodeoEndDate: string | null;
  entries: AdminEntryRow[];
}

function groupByOrgRodeo(rows: AdminEntryRow[]): RodeoGroupItem[] {
  const map = new Map<string, RodeoGroupItem>();
  for (const row of rows) {
    const key = `${row.organization_id ?? 'no-org'}::${row.rodeo_id ?? 'no-rodeo'}`;
    const existing = map.get(key);
    if (existing) {
      existing.entries.push(row);
      continue;
    }
    map.set(key, {
      key,
      organizationName: row.organization_name,
      rodeoId: row.rodeo_id,
      rodeoName: row.rodeo_name,
      rodeoStartDate: row.rodeo_start_date,
      rodeoEndDate: row.rodeo_end_date,
      entries: [row],
    });
  }
  // Sort by rodeo start date desc (most recent first) within each bucket so
  // the most relevant rodeo shows up at the top whether it's current or past.
  return Array.from(map.values()).sort((a, b) => {
    const ad = a.rodeoStartDate ? new Date(a.rodeoStartDate).getTime() : 0;
    const bd = b.rodeoStartDate ? new Date(b.rodeoStartDate).getTime() : 0;
    return bd - ad;
  });
}

function RodeoGroup({
  group,
  expandedRows,
  onToggleRow,
}: {
  group: RodeoGroupItem;
  expandedRows: Set<string>;
  onToggleRow: (id: string) => void;
}) {
  return (
    <div className="rounded-lg border bg-card text-card-foreground shadow-sm overflow-hidden">
      <div className="px-4 py-3 border-b bg-muted/30 flex items-center justify-between flex-wrap gap-2">
        <div className="min-w-0">
          <div className="text-xs uppercase tracking-wide text-muted-foreground">
            {group.organizationName ?? 'Unknown organization'}
          </div>
          <div className="font-semibold text-sm flex items-center gap-1.5 flex-wrap">
            {group.rodeoId ? (
              <Link
                to={`/rodeos/${group.rodeoId}`}
                className="hover:underline inline-flex items-center gap-1"
              >
                {group.rodeoName ?? 'Unnamed rodeo'}
                <ExternalLink className="h-3 w-3 opacity-50" />
              </Link>
            ) : (
              group.rodeoName ?? 'Unnamed rodeo'
            )}
            <span className="text-xs text-muted-foreground font-normal inline-flex items-center gap-1">
              <Calendar className="h-3 w-3" />
              {formatDateRange(group.rodeoStartDate, group.rodeoEndDate)}
            </span>
          </div>
        </div>
        <Badge variant="outline" className="text-xs">
          {group.entries.length} {group.entries.length === 1 ? 'entry' : 'entries'}
        </Badge>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/20 text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="text-left font-medium px-4 py-2">Event</th>
              <th className="text-left font-medium px-4 py-2">Status</th>
              <th className="text-right font-medium px-4 py-2">Fee</th>
              <th className="text-right font-medium px-4 py-2">Paid</th>
              <th className="text-left font-medium px-4 py-2">Payment</th>
              <th className="font-medium px-4 py-2" />
            </tr>
          </thead>
          <tbody className="divide-y">
            {group.entries.map((entry) => (
              <EntryRow
                key={entry.registration_id}
                entry={entry}
                expanded={expandedRows.has(entry.registration_id)}
                onToggle={onToggleRow}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function EntryRow({
  entry,
  expanded,
  onToggle,
}: {
  entry: AdminEntryRow;
  expanded: boolean;
  onToggle: (id: string) => void;
}) {
  const navigate = useNavigate();
  const meta = STATUS_META[entry.payment_status];
  // Cancelled rows fade so the eye skips past them — they shouldn't compete
  // visually with active outstanding balances. Keep them visible though;
  // admins still need to be able to answer "did this person enter and back
  // out" questions.
  const cancelledCls = entry.is_cancelled ? 'opacity-50' : '';
  const handleRowClick = () => onToggle(entry.registration_id);
  // The View action navigates to /events; the chevron + row click toggle the
  // inline detail panel. We stop propagation on the action cell so a click
  // there doesn't also toggle the row.
  const stopProp = (e: React.MouseEvent) => e.stopPropagation();
  const navigateToEvent = () => {
    if (entry.event_id) navigate(`/events/${entry.event_id}`);
  };
  return (
    <>
      <tr
        className={`${cancelledCls} cursor-pointer transition-colors hover:bg-muted/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset`.trim()}
        onClick={handleRowClick}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleRowClick();
          }
        }}
        tabIndex={0}
        role="button"
        aria-expanded={expanded}
        aria-label={`Toggle details for ${entry.event_name?.trim() || entry.event_type?.trim() || 'event'}`}
      >
        <td className="px-4 py-2.5">
          <div className="flex items-start gap-2">
            <span
              aria-hidden
              className="mt-0.5 text-muted-foreground shrink-0"
              title={expanded ? 'Collapse details' : 'Expand details'}
            >
              {expanded ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
            </span>
            <div className="min-w-0">
              <div className="font-medium">
                {/* `||` (not `??`) so a blank/empty event_name falls through to
                    the discipline name — empty strings are not "present". */}
                {entry.event_name?.trim() || entry.event_type?.trim() || '—'}
              </div>
              <div className="text-xs text-muted-foreground space-x-2">
                {entry.position && <span>{entry.position}</span>}
                {entry.registration_type && <span>{entry.registration_type}</span>}
                {entry.partner_name && <span>w/ {entry.partner_name}</span>}
              </div>
            </div>
          </div>
        </td>
        <td className="px-4 py-2.5">
          <Badge variant="outline" className="text-xs">
            {entry.is_cancelled ? 'cancelled' : entry.registration_status ?? 'unknown'}
          </Badge>
        </td>
        <td className="px-4 py-2.5 text-right tabular-nums">
          {entry.amount_due > 0 ? formatCurrency(entry.amount_due) : '—'}
        </td>
        <td className="px-4 py-2.5 text-right tabular-nums">
          {/* Paid cell prefers lifetime gross when present so cancelled-but-
              once-paid entries don't collapse to "—". Net (amount_paid) is
              still useful but loses the historical signal when a refund
              landed on the row. */}
          {entry.amount_paid_gross > 0.01 ? (
            <div className="flex flex-col items-end leading-tight">
              <span>{formatCurrency(entry.amount_paid_gross)}</span>
              {entry.amount_refunded > 0.01 && (
                <span className="text-xs text-rose-700 dark:text-rose-300">
                  −{formatCurrency(entry.amount_refunded)} refunded
                </span>
              )}
            </div>
          ) : (
            '—'
          )}
        </td>
        <td className="px-4 py-2.5">
          <div className="flex items-center gap-1.5 flex-wrap">
            <Badge
              variant="outline"
              className={`text-xs text-center justify-center ${meta.cls}`}
            >
              {meta.label}
            </Badge>
            {/* Stripe / HighCall "how it was handled" badge — only once the
                entry has actually been paid and the method resolves to a
                provider (pay-later resolves to null). */}
            {(entry.payment_status === 'paid' ||
              entry.payment_status === 'partial') &&
              (() => {
                const provider = deriveProvider(entry.payment_method);
                return provider ? <ProviderBadge provider={provider} /> : null;
              })()}
          </div>
        </td>
        <td className="px-4 py-2.5 text-right" onClick={stopProp}>
          {entry.event_id && (
            <button
              type="button"
              onClick={navigateToEvent}
              className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded px-1"
            >
              View
              <ExternalLink className="h-3 w-3" />
            </button>
          )}
        </td>
      </tr>
      {expanded && (
        <tr className="bg-muted/20">
          <td colSpan={6} className="px-6 py-4 border-t border-border/60">
            <EntryDetailsPanel entry={entry} />
          </td>
        </tr>
      )}
    </>
  );
}

// ─── Detail-panel helpers (shared shape with EntriesLedgerPage) ────────────
const PAYMENT_METHOD_META: Record<
  LedgerPaymentMethod,
  { label: string; icon: typeof CreditCard }
> = {
  stripe_card: { label: 'Stripe (card)', icon: CreditCard },
  bank: { label: 'Bank (ACH)', icon: Building2 },
  cash: { label: 'Cash', icon: Banknote },
  check: { label: 'Check', icon: FileCheck },
  pay_later: { label: 'Pay later', icon: Clock },
};

const CREATOR_ROLE_META: Record<LedgerCreatedByRole, string> = {
  self: 'signed themselves up',
  buddy_group_primary: 'buddy group primary',
  team_roping_member: 'team roping partner',
};

function EntryDetailsPanel({ entry }: { entry: AdminEntryRow }) {
  const pm = entry.payment_method
    ? PAYMENT_METHOD_META[entry.payment_method]
    : null;
  // A $0 entry collects nothing, so the method it was signed up under is
  // history rather than a standing obligation. Announcing "Pay later —
  // contestant chose to settle in person" beside "No fee due on this entry"
  // contradicts it, so the signup choice moves into the hint.
  //
  // 'free' covers BOTH an admin waive and an event that never charged, and the
  // row carries no override flag to tell them apart — so the copy must hold
  // for either.
  const isFree = entry.payment_status === 'free';
  const PmIcon = isFree ? Gift : (pm?.icon ?? HelpCircle);
  const isPaid = !!entry.paid_by;
  // Highlight the partner-paid case explicitly. On the user-details page we
  // know the contestant's id (the page's user); whenever paid_by points at
  // someone else, the partner covered the fee.
  const paidByPartner =
    !!entry.paid_by &&
    !!entry.user_profile_id &&
    entry.paid_by.user_id !== entry.user_profile_id;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <DetailCard
          icon={<PmIcon className="h-4 w-4" />}
          label="Payment method"
          value={isFree ? 'None needed' : (pm?.label ?? 'Unknown')}
          hint={
            isFree
              ? `Entry totals $0 — nothing to collect.${
                  pm ? ` Chose ${pm.label} at signup.` : ''
                }`
              : entry.payment_method === 'pay_later'
              ? 'Contestant chose to settle in person.'
              : entry.payment_method === 'cash'
                ? 'Recorded as a cash payment.'
                : entry.payment_method === 'check'
                  ? 'Recorded as a check payment.'
                  : entry.payment_status === 'verifying'
                  ? 'ACH bank verification in progress — contestant must confirm two micro-deposits before the debit is submitted.'
                  : entry.payment_status === 'processing'
                    ? 'Payment submitted — funds still clearing (bank transfers take ~4 business days).'
                    : entry.payment_method === 'bank'
                      ? 'Settled online via Stripe (bank transfer / ACH).'
                      : entry.payment_method
                        ? 'Settled online via Stripe.'
                        : 'No method recorded on this entry.'
          }
        />
        <DetailCard
          icon={<User className="h-4 w-4" />}
          label="Created by"
          value={entry.created_by?.name ?? 'Unknown'}
          hint={
            entry.created_by
              ? `as ${CREATOR_ROLE_META[entry.created_by.role]}`
              : 'No creator recorded on this entry.'
          }
          accentIcon={
            entry.created_by?.role === 'buddy_group_primary' ? (
              <Users className="h-3.5 w-3.5" />
            ) : entry.created_by?.role === 'team_roping_member' ? (
              <UserCheck className="h-3.5 w-3.5" />
            ) : null
          }
        />
        {isPaid ? (
          <DetailCard
            icon={<UserCheck className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />}
            label="Paid by"
            value={entry.paid_by!.name}
            hint={
              paidByPartner
                ? 'Team-roping partner covered this entry.'
                : "This entry's fee has cleared."
            }
            accentIcon={paidByPartner ? <Users className="h-3.5 w-3.5" /> : null}
            tone="paid"
          />
        ) : (
          <DetailCard
            icon={<User className="h-4 w-4 text-amber-600 dark:text-amber-400" />}
            label="Responsible for payment"
            value={entry.responsible_party?.name ?? 'Unknown'}
            hint={
              entry.responsible_party
                ? entry.amount_due > 0
                  ? 'Owed on this entry until paid.'
                  : 'No fee due on this entry.'
                : 'No responsible party recorded.'
            }
            tone="unpaid"
          />
        )}
      </div>
      <FeeBreakdownPanel entry={entry} />
      <TenderSplitPanel entry={entry} />
      <MarkEntryPaidButton
        registrationId={entry.registration_id}
        paymentStatus={entry.payment_status}
        isCancelled={entry.is_cancelled}
        amountDue={entry.amount_due}
        feeBreakdown={entry.fee_breakdown}
      />
      <EntryAdminActions
        registrationId={entry.registration_id}
        paymentStatus={entry.payment_status}
        isCancelled={entry.is_cancelled}
        rodeoCancelled={entry.rodeo_cancelled}
        wasPaid={entry.was_paid}
        feeBreakdown={entry.fee_breakdown}
      />
    </div>
  );
}

function FeeBreakdownPanel({ entry }: { entry: AdminEntryRow }) {
  const fb = entry.fee_breakdown;
  if (!fb) {
    return (
      <div className="rounded-md border border-border bg-card/60 p-3">
        <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-muted-foreground mb-2">
          <CircleDollarSign className="h-4 w-4" />
          <span>Fee breakdown</span>
        </div>
        <p className="text-sm text-muted-foreground">
          Detail not available for this entry.
        </p>
      </div>
    );
  }
  const lines: Array<{ label: string; value: number; hint?: string }> = [
    { label: 'Entry fee', value: fb.entry_fee },
    {
      label: 'Transaction fee',
      value: fb.transaction_fee,
      hint: fb.transaction_fee > 0 ? 'Online payment surcharge.' : undefined,
    },
  ];
  if (fb.non_member_fee > 0) {
    lines.push({
      label: 'Non-member fee',
      value: fb.non_member_fee,
      hint: 'Charged because the contestant is not an active member.',
    });
  }
  return (
    <div className="rounded-md border border-border bg-card/60 p-3">
      <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-muted-foreground mb-2">
        <CircleDollarSign className="h-4 w-4" />
        <span>Fee breakdown</span>
      </div>
      <dl className="space-y-1">
        {lines.map((line) => (
          <div
            key={line.label}
            className="flex items-baseline justify-between gap-3 text-sm"
          >
            <dt className="text-muted-foreground">
              {line.label}
              {line.hint && (
                <span className="block text-xs text-muted-foreground/70">
                  {line.hint}
                </span>
              )}
            </dt>
            <dd className="tabular-nums font-medium">
              {formatCurrency(line.value)}
            </dd>
          </div>
        ))}
        <div className="flex items-baseline justify-between gap-3 text-sm pt-2 border-t border-border/60 mt-2">
          <dt className="font-medium">Total</dt>
          <dd className="tabular-nums font-bold">
            {formatCurrency(fb.total)}
          </dd>
        </div>
      </dl>
    </div>
  );
}

// Split-tender breakdown — only rendered when part of the entry was settled
// with org account credit at checkout. Shows the two legs ("Stripe $130 +
// HighCall credit $25") so the fee doesn't read as a single opaque online
// payment when it wasn't.
function TenderSplitPanel({ entry }: { entry: AdminEntryRow }) {
  const credit = entry.credit_applied ?? 0;
  if (credit <= 0) return null;
  const cardPortion = Math.max(0, (entry.amount_paid_gross ?? 0) - credit);
  const provider = deriveProvider(entry.payment_method) ?? 'stripe';
  const pm = entry.payment_method
    ? PAYMENT_METHOD_META[entry.payment_method]
    : null;
  return (
    <div className="rounded-md border border-border bg-card/60 p-3">
      <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-muted-foreground mb-2">
        <CircleDollarSign className="h-4 w-4" />
        <span>Paid with</span>
      </div>
      <div className="space-y-1.5">
        {cardPortion > 0 && (
          <div className="flex items-center justify-between gap-3 text-sm">
            <span className="inline-flex items-center gap-2 text-muted-foreground">
              <ProviderBadge provider={provider} />
              {pm?.label ?? (provider === 'stripe' ? 'Card / bank' : 'Offline')}
            </span>
            <span className="tabular-nums font-medium">
              {formatCurrency(cardPortion)}
            </span>
          </div>
        )}
        <div className="flex items-center justify-between gap-3 text-sm">
          <span className="inline-flex items-center gap-2 text-muted-foreground">
            <ProviderBadge provider="highcall" />
            Account credit
          </span>
          <span className="tabular-nums font-medium text-emerald-700 dark:text-emerald-300">
            {formatCurrency(credit)}
          </span>
        </div>
      </div>
    </div>
  );
}

function DetailCard({
  icon,
  label,
  value,
  hint,
  accentIcon,
  tone,
}: {
  icon: ReactNode;
  label: string;
  value: string;
  hint?: ReactNode;
  accentIcon?: ReactNode;
  tone?: 'paid' | 'unpaid';
}) {
  const toneCls =
    tone === 'paid'
      ? 'border-emerald-500/30'
      : tone === 'unpaid'
        ? 'border-amber-500/30'
        : 'border-border';
  return (
    <div className={`rounded-md border ${toneCls} bg-card/60 p-3 flex flex-col gap-1`}>
      <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {icon}
        <span>{label}</span>
      </div>
      <div className="text-sm font-medium flex items-center gap-2">
        <span>{value}</span>
        {accentIcon && (
          <span className="text-muted-foreground" aria-hidden>
            {accentIcon}
          </span>
        )}
      </div>
      {hint && (
        <div className="text-xs text-muted-foreground leading-snug">{hint}</div>
      )}
    </div>
  );
}
